import React, { useEffect, useState } from 'react';
import { ArrowUpRight, ExternalLink, Instagram } from 'lucide-react';
import { PROJECTS } from '../mock';

export default function SelectedWork() {
  const [activeId, setActiveId] = useState(PROJECTS[0].id);
  const active = PROJECTS.find((p) => p.id === activeId);

  // Deep-link from the command palette: "sirin:work-focus"
  useEffect(() => {
    const handler = (e) => {
      const id = e?.detail?.id;
      if (id && PROJECTS.some((p) => p.id === id)) setActiveId(id);
    };
    window.addEventListener('sirin:work-focus', handler);
    return () => window.removeEventListener('sirin:work-focus', handler);
  }, []);

  return (
    <section
      id="work"
      className="py-24 md:py-32"
      style={{ background: 'var(--bone-soft)' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between gap-6 mb-14 md:mb-20 flex-wrap">
          <div>
            <div className="mono text-[11px] uppercase tracking-[0.2em] mb-4" style={{ color: 'var(--terracotta)' }}>
              § 02 · Selected work
            </div>
            <h2
              className="display"
              style={{
                fontSize: 'clamp(2rem, 4.5vw, 3.6rem)',
                lineHeight: 1.05,
                fontWeight: 400,
                color: 'var(--ink)',
              }}
            >
              Things I stayed with{' '}
              <em className="italic serif" style={{ color: 'var(--forest)' }}>long enough to matter.</em>
            </h2>
          </div>
          <div className="mono text-[12px] tracking-wide" style={{ color: 'var(--moss)' }}>
            {PROJECTS.length} projects · click to preview
          </div>
        </div>

        {/* Index list */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-5">
            <ul className="divide-y" style={{ borderColor: 'rgba(15,27,20,0.1)' }}>
              {PROJECTS.map((p, i) => {
                const isActive = p.id === activeId;
                return (
                  <li key={p.id} style={{ borderTop: i === 0 ? '1px solid rgba(15,27,20,0.1)' : undefined, borderBottom: '1px solid rgba(15,27,20,0.1)' }}>
                    <button
                      onClick={() => setActiveId(p.id)}
                      className="w-full text-left py-5 md:py-6 flex items-start gap-5 group"
                    >
                      <span
                        className="mono text-[11px] pt-1.5 w-8 shrink-0"
                        style={{ color: isActive ? 'var(--terracotta)' : 'var(--stone)' }}
                      >
                        0{i + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <h3
                            className="display"
                            style={{
                              fontSize: 'clamp(1.4rem, 2.2vw, 1.9rem)',
                              fontWeight: 500,
                              color: isActive ? 'var(--forest)' : 'var(--ink)',
                              lineHeight: 1.1,
                            }}
                          >
                            {p.name}
                          </h3>
                          <span className="mono text-[10px] uppercase tracking-[0.16em]" style={{ color: 'var(--moss)' }}>
                            {p.role}
                          </span>
                        </div>
                        <p
                          className="mt-2 text-[14px] leading-relaxed"
                          style={{ color: isActive ? 'var(--forest-soft)' : 'var(--moss)' }}
                        >
                          {p.tagline}
                        </p>
                      </div>
                      <ArrowUpRight
                        size={18}
                        className="mt-1 shrink-0"
                        style={{
                          color: isActive ? 'var(--forest)' : 'var(--stone)',
                          transform: isActive ? 'rotate(0deg)' : 'rotate(0deg)',
                        }}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Preview pane */}
          <div className="md:col-span-7">
            <div
              className="sticky top-24 rounded-2xl overflow-hidden"
              style={{
                background: 'var(--cream)',
                border: '1px solid rgba(15,27,20,0.1)',
                boxShadow: '0 30px 80px -40px rgba(15,27,20,0.22)',
              }}
            >
              {/* Browser chrome */}
              <div
                className="flex items-center gap-2 px-4 py-3 border-b"
                style={{ borderColor: 'rgba(15,27,20,0.08)', background: 'var(--bone-soft)' }}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#d9ccb8' }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#c9bda8' }} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#b8ae9a' }} />
                <div
                  className="ml-3 flex-1 mono text-[11px] px-3 py-1 rounded-md"
                  style={{ background: 'var(--bone)', color: 'var(--moss)' }}
                >
                  {active.url.replace(/^https?:\/\//, '')}
                </div>
                <a
                  href={active.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 mono text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded"
                  style={{ color: 'var(--forest)' }}
                  aria-label="Open live site"
                >
                  Open <ExternalLink size={11} />
                </a>
              </div>

              {/* Live embed or fallback */}
              <div className="relative" style={{ aspectRatio: '16/10', background: 'var(--bone)' }}>
                {active.embed ? (
                  <iframe
                    key={active.id}
                    src={active.embed}
                    title={active.name}
                    className="absolute inset-0 w-full h-full"
                    loading="lazy"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                  />
                ) : (
                  <FallbackCard project={active} />
                )}
              </div>

              {/* Context */}
              <div className="p-6 md:p-8">
                <div className="mono text-[11px] uppercase tracking-[0.18em] mb-3" style={{ color: 'var(--terracotta)' }}>
                  {active.kicker}
                </div>
                <p className="text-[15px] leading-relaxed mb-5" style={{ color: 'var(--forest-soft)' }}>
                  {active.context}
                </p>
                <div className="flex flex-wrap gap-2">
                  {active.outcome.map((o) => (
                    <span
                      key={o}
                      className="mono text-[11px] px-3 py-1.5 rounded-full"
                      style={{
                        background: 'var(--bone-deep)',
                        color: 'var(--forest)',
                      }}
                    >
                      {o}
                    </span>
                  ))}
                </div>

                {(active.instagram || active.url) && (
                  <div className="mt-6 flex items-center gap-5 text-[13px]">
                    {active.url && (
                      <a
                        href={active.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 link-line mono uppercase tracking-[0.14em] text-[11px]"
                        style={{ color: 'var(--forest)' }}
                      >
                        Visit site <ArrowUpRight size={13} />
                      </a>
                    )}
                    {active.instagram && (
                      <a
                        href={active.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 link-line mono uppercase tracking-[0.14em] text-[11px]"
                        style={{ color: 'var(--terracotta)' }}
                      >
                        <Instagram size={13} /> Instagram
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FallbackCard({ project }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-10" style={{ background: `linear-gradient(135deg, ${project.accent}18, ${project.accent}05)` }}>
      <div
        className="mono text-[10px] uppercase tracking-[0.2em] mb-4 px-3 py-1 rounded-full"
        style={{ background: 'var(--cream)', color: project.accent }}
      >
        App Store · no embed
      </div>
      <h4
        className="display mb-3"
        style={{
          fontSize: 'clamp(2rem, 4.5vw, 3rem)',
          color: project.accent,
          fontWeight: 500,
        }}
      >
        {project.name}
      </h4>
      <p className="text-[14px] max-w-md" style={{ color: 'var(--forest-soft)' }}>
        {project.tagline}
      </p>
      <a
        href={project.url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.16em] px-4 py-2 rounded-full"
        style={{ background: project.accent, color: 'var(--cream)' }}
      >
        View on App Store <ArrowUpRight size={13} />
      </a>
    </div>
  );
}
